/**
 * Reports > Periodical Dispatch (user, 2026-10-28): every lot the plant (or a dryer) sent out in a
 * period, with where it went, the truck, its gate pass out and the lot's QC and GOT. No legacy
 * screen; it sits with the Arrival reports among the hub's key reports.
 */
import type {
  ReportCellFormat,
  ReportColumn,
  ReportDef,
} from "./warehouse-reports";

export type DispatchReportKey = "dispatch-periodical" | OrderDispatchReportKey;

/**
 * Orders and Dispatch reports (user, 2026-10-30: "build all of them"): SeedTrac's Order Booking and
 * Dispatch report menus, on the Orders and the sales dispatches. Each has its own code (permissions.ts
 * `dispatch.reports.*`); the route is /reports/orders-dispatch/:key.
 */
export type OrderDispatchReportKey =
  | "orders-pending"
  | "orders-booked"
  | "orders-cancelled"
  | "orders-suspended"
  | "dispatch-dc"
  | "dispatch-crop-variety"
  | "dispatch-cnf-barcodes"
  | "pack-activity"
  | "pack-ageing";

/** The dispatch forms the report reads, by its "Dispatch Type" picker. */
export const DISPATCH_REPORT_TYPES = {
  st: { label: "Stock Transfer", forms: ["dispatch_stock_transfer"] },
  cob: { label: "Cob Dispatch to Plant", forms: ["cob_dispatch"] },
  // Orders and Dispatch (0181): the sales dispatches, to a party.
  pack: { label: "Pack Seed Dispatch", forms: ["dispatch_pack"] },
  bulk: { label: "Bulk Seed Dispatch", forms: ["dispatch_bulk"] },
  tdf: { label: "TDF Seed Dispatch", forms: ["dispatch_tdf"] },
  discard: { label: "Material Discard", forms: ["dispatch_discard"] },
} as const;
export type DispatchReportType = keyof typeof DISPATCH_REPORT_TYPES;

const c = (
  key: string,
  label: string,
  format: ReportCellFormat,
  more: Partial<Omit<ReportColumn, "key" | "label" | "format">> = {}
): ReportColumn => ({
  key,
  label,
  format,
  align:
    format === "int" || format === "qty" || format === "pct"
      ? "right"
      : format === "text"
        ? "left"
        : "center",
  ...more,
});

const PERIODICAL_DISPATCH: {
  "dispatch-periodical": ReportDef & { key: "dispatch-periodical" };
} = {
  "dispatch-periodical": {
    key: "dispatch-periodical",
    menu: "Periodical Dispatch Report",
    title: "Periodical Dispatch Report",
    asOn: false,
    kind: "flat",
    filters: ["period", "destPlant", "crop", "variety"],
    choices: [
      {
        param: "kind",
        label: "Dispatch Type",
        options: [
          { value: "all", label: "All dispatch types" },
          { value: "st", label: DISPATCH_REPORT_TYPES.st.label },
          { value: "cob", label: DISPATCH_REPORT_TYPES.cob.label },
          { value: "pack", label: DISPATCH_REPORT_TYPES.pack.label },
          { value: "bulk", label: DISPATCH_REPORT_TYPES.bulk.label },
          { value: "tdf", label: DISPATCH_REPORT_TYPES.tdf.label },
          { value: "discard", label: DISPATCH_REPORT_TYPES.discard.label },
        ],
      },
    ],
    columns: [
      c("serial", "#", "int", { align: "center" }),
      c("dispatchedOn", "Dispatch Date", "date"),
      c("dispatchType", "Dispatch Type", "text"),
      c("docNo", "Transaction ID", "text"),
      c("fromFacility", "From", "text"),
      c("destPlant", "Destination", "text"),
      c("vehicleNo", "Vehicle No.", "text"),
      c("gatePassNo", "Gate Pass Out No.", "text"),
      c("crop", "Crop", "text"),
      c("variety", "Variety", "text"),
      c("lotNumber", "Lot No.", "text"),
      c("stage", "Stage", "text", { align: "center" }),
      c("bags", "NoB", "int", { total: true }),
      c("qty", "Qty", "qty", { total: true }),
      c("qcStatus", "QC Status", "text", { align: "center" }),
      c("germinationPct", "Germ %", "pct"),
      c("testedOn", "DoT", "date"),
      c("gotStatus", "GOT Status", "text", { align: "center" }),
      c("dogr", "DoGR", "date"),
      c("seedStatus", "Seed Status", "text", { align: "center" }),
      c("received", "Received", "text", { align: "center" }),
    ],
    tooMany: "Too many lots. Shorten the dates or choose a crop.",
    note: "Submitted dispatches in the period: Stock Transfers out of the plant, the dryers' Cob Dispatch to Plant, and Pack Seed, Bulk, TDF and Material Discard dispatches to parties (Destination is the party). QC Status, Germ % and DoT are the lot's latest lab result; GOT follows the lot's first arrival. Received shows the date the destination submitted its receipt.",
  },
};

const ALL_FIRST = { value: "all", label: "All" };
const ORDER_TYPE_CHOICE = {
  param: "kind",
  label: "Order Type",
  options: [
    { value: "all", label: "All order types" },
    { value: "sales", label: "Sales" },
    { value: "stock_transfer", label: "Stock Transfer" },
    { value: "tdf", label: "Trial / Demo / Free" },
  ],
} as const;

const ORDER_COLS = [
  c("serial", "#", "int", { align: "center" }),
  c("orderDate", "Date", "date"),
  c("orderNo", "Order No.", "text"),
  c("orderType", "Order Type", "text"),
  c("partyOrderRef", "PO Ref. No.", "text"),
  c("partyType", "Party Type", "text"),
  c("party", "Party", "text"),
  c("location", "Location", "text"),
  c("state", "State", "text"),
];

/** The Orders and Dispatch reports, by key (dispatch-reports.ts builds them). */
export const ORDER_DISPATCH_REPORTS: {
  [K in OrderDispatchReportKey]: ReportDef & { key: K };
} = {
  "orders-pending": {
    key: "orders-pending",
    menu: "Pending Order Report",
    title: "Pending Order Report",
    asOn: true,
    kind: "sectioned",
    sectionLabels: ["Party / Crop", "Location / Variety"],
    filters: ["crop", "variety"],
    choices: [
      {
        param: "mode",
        label: "Report",
        options: [
          { value: "party", label: "Party wise" },
          { value: "crop", label: "Crop-Variety wise" },
          { value: "all", label: "All party consolidated" },
        ],
      },
      ORDER_TYPE_CHOICE,
    ],
    columns: [
      ...ORDER_COLS,
      c("crop", "Crop", "text"),
      c("variety", "Variety", "text"),
      c("ups", "UPS", "text", { align: "center" }),
      c("ordered", "Ordered", "qty", { total: true }),
      c("dispatched", "Dispatched", "qty", { total: true }),
      c("balance", "Balance", "qty", { total: true }),
      c("held", "On Hold", "text", { align: "center" }),
    ],
    tooMany: "Too many order items. Choose a crop or order type.",
    note: "Approved orders with a balance still to send, as on today. Quantities in kg.",
  },
  "orders-booked": {
    key: "orders-booked",
    menu: "Periodical Booked Order Report",
    title: "Periodical Booked Order Report",
    asOn: false,
    kind: "flat",
    filters: ["period"],
    choices: [ORDER_TYPE_CHOICE],
    columns: [
      ...ORDER_COLS,
      c("consignee", "Consignee", "text"),
      c("items", "Items", "int"),
      c("ordered", "Ordered", "qty", { total: true }),
      c("dispatched", "Dispatched", "qty", { total: true }),
      c("status", "Status", "text", { align: "center" }),
    ],
    tooMany: "Too many orders. Shorten the dates.",
    note: "Every order booked in the period (by order date), drafts included. Quantities in kg.",
  },
  "orders-cancelled": {
    key: "orders-cancelled",
    menu: "Cancel Order Report",
    title: "Cancelled and Short-closed Order Report",
    asOn: false,
    kind: "flat",
    filters: ["period"],
    choices: [
      {
        param: "mode",
        label: "Show",
        options: [
          { value: "all", label: "Cancelled and short-closed" },
          { value: "cancelled", label: "Cancelled" },
          { value: "closed", label: "Short-closed" },
        ],
      },
      ORDER_TYPE_CHOICE,
    ],
    columns: [
      ...ORDER_COLS,
      c("ordered", "Ordered", "qty", { total: true }),
      c("dispatched", "Dispatched", "qty", { total: true }),
      c("status", "Status", "text", { align: "center" }),
      c("closedOn", "On", "date"),
      c("closedBy", "By", "text"),
      c("reason", "Reason", "text"),
    ],
    tooMany: "Too many orders. Shorten the dates.",
    note: "Orders cancelled or short-closed in the period (by the day it was done). Quantities in kg.",
  },
  "orders-suspended": {
    key: "orders-suspended",
    menu: "Suspended Order Report",
    title: "Suspended Order Report",
    asOn: true,
    kind: "flat",
    filters: [],
    choices: [
      {
        param: "mode",
        label: "Pending for",
        options: [
          { value: "month", label: "More than 30 days" },
          { value: "two_months", label: "More than 60 days" },
          { value: "three_months", label: "More than 90 days" },
          { value: "six_months", label: "More than 180 days" },
          { value: "any", label: "Any age" },
        ],
      },
      ORDER_TYPE_CHOICE,
    ],
    columns: [
      ...ORDER_COLS,
      c("ordered", "Ordered", "qty", { total: true }),
      c("dispatched", "Dispatched", "qty", { total: true }),
      c("balance", "Balance", "qty", { total: true }),
      c("lastDispatch", "Last Dispatch", "date"),
      c("ageDays", "Pending (days)", "int"),
    ],
    tooMany: "Too many orders. Choose an order type.",
    note: "Approved orders still holding a balance, by how long since they were booked. Quantities in kg.",
  },
  "dispatch-dc": {
    key: "dispatch-dc",
    menu: "Periodical Dispatch Report - DC wise",
    title: "Periodical Dispatch Report - DC wise",
    asOn: false,
    kind: "flat",
    filters: ["period"],
    choices: [
      {
        param: "kind",
        label: "Dispatch Type",
        options: [
          { value: "all", label: "All dispatch types" },
          { value: "pack", label: "Pack Seed Dispatch" },
          { value: "bulk", label: "Bulk Seed Dispatch" },
          { value: "tdf", label: "TDF Seed Dispatch" },
          { value: "discard", label: "Material Discard" },
          { value: "st", label: "Stock Transfer" },
        ],
      },
    ],
    columns: [
      c("serial", "#", "int", { align: "center" }),
      c("dispatchedOn", "Dispatch Date", "date"),
      c("docNo", "DC No.", "text"),
      c("dispatchType", "Dispatch Type", "text"),
      c("party", "Party / Destination", "text"),
      c("location", "Location", "text"),
      c("state", "State", "text"),
      c("orderNos", "Order No(s).", "text"),
      c("transporter", "Transport / Courier", "text"),
      c("vehicleNo", "Vehicle No.", "text"),
      c("lrNo", "LR / Docket No.", "text"),
      c("gatePassNo", "Gate Pass Out", "text"),
      c("bags", "NoB / NoMP", "int", { total: true }),
      c("qty", "Total Qty", "qty", { total: true }),
      c("grossKg", "Gross Wt.", "qty", { total: true }),
    ],
    tooMany: "Too many dispatches. Shorten the dates.",
    note: "One row per submitted dispatch in the period. Quantities in kg; gross weight from the master packs' scale weights.",
  },
  "dispatch-crop-variety": {
    key: "dispatch-crop-variety",
    menu: "Consolidated CV wise Dispatch Report",
    title: "Consolidated Crop Variety wise Dispatch Report",
    asOn: false,
    kind: "flat",
    filters: ["period", "crop", "variety"],
    choices: [
      {
        param: "kind",
        label: "Dispatch Type",
        options: [
          { value: "all", label: "All sales dispatches" },
          { value: "pack", label: "Pack Seed Dispatch" },
          { value: "bulk", label: "Bulk Seed Dispatch" },
          { value: "tdf", label: "TDF Seed Dispatch" },
        ],
      },
    ],
    columns: [
      c("serial", "#", "int", { align: "center" }),
      c("crop", "Crop", "text"),
      c("variety", "Variety", "text"),
      c("classification", "Variety Type", "text", { align: "center" }),
      c("ups", "UPS", "text", { align: "center" }),
      c("dispatches", "Dispatches", "int", { total: true }),
      c("bags", "NoB / NoMP", "int", { total: true }),
      c("qty", "Qty", "qty", { total: true }),
    ],
    tooMany: "Too many rows. Choose a crop.",
    note: "What left the plant to parties in the period, summed by crop, variety and UPS. Quantities in kg.",
  },
  "dispatch-cnf-barcodes": {
    key: "dispatch-cnf-barcodes",
    menu: "CV wise C&F Dispatch Report",
    title: "Crop Variety wise C&F Dispatch Report",
    asOn: false,
    kind: "flat",
    filters: ["period", "crop", "variety"],
    choices: [
      {
        param: "kind",
        label: "Party",
        options: [{ value: "cnf", label: "C&F and Branches" }, ALL_FIRST],
      },
    ],
    columns: [
      c("serial", "#", "int", { align: "center" }),
      c("dispatchedOn", "Dispatch Date", "date"),
      c("docNo", "DC No.", "text"),
      c("party", "Party", "text"),
      c("location", "Location", "text"),
      c("state", "State", "text"),
      c("crop", "Crop", "text"),
      c("variety", "Variety", "text"),
      c("lotNumber", "Lot No.", "text"),
      c("ups", "UPS", "text", { align: "center" }),
      c("barcode", "Barcode", "text"),
      c("pouches", "NoP", "int", { total: true }),
      c("qty", "Net Wt.", "qty", { total: true }),
      c("grossKg", "Gross Wt.", "qty", { total: true }),
    ],
    tooMany: "Too many master packs. Shorten the dates or choose a crop.",
    note: "Every master pack on a submitted Pack Seed dispatch in the period, by barcode. Weights in kg.",
  },
  "pack-activity": {
    key: "pack-activity",
    menu: "Pack Seed Activity Report",
    title: "Pack Seed Activity Report",
    asOn: false,
    kind: "flat",
    filters: ["period", "crop", "variety"],
    columns: [
      c("serial", "#", "int", { align: "center" }),
      c("crop", "Crop", "text"),
      c("variety", "Variety", "text"),
      c("lotNumber", "Lot No.", "text"),
      c("opening", "Opening", "qty", { total: true }),
      c("packed", "Packed", "qty", { total: true }),
      c("received", "Received", "qty", { total: true }),
      c("dispatched", "Dispatched", "qty", { total: true }),
      c("transferred", "Transferred out", "qty", { total: true }),
      c("other", "Other out", "qty", { total: true }),
      c("closing", "Closing", "qty", { total: true }),
      c("closingMp", "Closing NoMP", "int", { total: true }),
    ],
    tooMany: "Too many lots. Choose a crop or variety.",
    note: "Packed (P stage) seed at the plant: what each lot held at the start of the period, what came in (packed, received) and went out (dispatched to parties, transferred to plants, anything else), and what it held at the end. Kg from the stock ledger.",
  },
  "pack-ageing": {
    key: "pack-ageing",
    menu: "Variety wise Pack Seed Report",
    title: "Variety wise Pack Seed Report (Ageing)",
    asOn: true,
    kind: "sectioned",
    filters: ["crop", "variety"],
    choices: [
      {
        param: "mode",
        label: "Packed since",
        options: [
          { value: "any", label: "All" },
          { value: "month", label: "1 month and more" },
          { value: "two_months", label: "2 months and more" },
          { value: "three_months", label: "3 months and more" },
          { value: "six_months", label: "6 months and more" },
        ],
      },
    ],
    columns: [
      c("serial", "#", "int", { align: "center" }),
      c("lotNumber", "Lot No.", "text"),
      c("packedOn", "Packed / DoT", "date"),
      c("validUpto", "Valid Upto", "date"),
      c("ageDays", "Age (days)", "int"),
      c("daysLeft", "Validity left (days)", "int"),
      c("bags", "NoMP", "int", { total: true }),
      c("qty", "Qty", "qty", { total: true }),
      c("slocs", "SLOC", "slocs"),
    ],
    tooMany: "Too many lots. Choose a crop or variety.",
    note: "Packed (P stage) seed in the plant's bins, as on today, by how long ago it was packed (its Pack DoT). Kg.",
  },
};

export const DISPATCH_REPORTS: {
  [K in DispatchReportKey]: ReportDef & { key: K };
} = { ...PERIODICAL_DISPATCH, ...ORDER_DISPATCH_REPORTS };

/** Each Orders and Dispatch report's code (permissions.ts), by report key. */
export const ORDER_DISPATCH_REPORT_PERMISSION = {
  "orders-pending": "dispatch.reports.pending_orders",
  "orders-booked": "dispatch.reports.booked_orders",
  "orders-cancelled": "dispatch.reports.cancelled_orders",
  "orders-suspended": "dispatch.reports.suspended_orders",
  "dispatch-dc": "dispatch.reports.dc_wise",
  "dispatch-crop-variety": "dispatch.reports.crop_variety",
  "dispatch-cnf-barcodes": "dispatch.reports.cnf_barcodes",
  "pack-activity": "dispatch.reports.pack_activity",
  "pack-ageing": "dispatch.reports.pack_ageing",
} as const satisfies Record<OrderDispatchReportKey, string>;

export const ORDER_DISPATCH_REPORT_KEYS = Object.keys(
  ORDER_DISPATCH_REPORT_PERMISSION
) as OrderDispatchReportKey[];

/** The ageing pickers' values, in days (choice values are words: REPORT_CHOICE_PARAMS). */
export const AGE_CHOICE_DAYS: Record<string, number> = {
  any: 0,
  month: 30,
  two_months: 60,
  three_months: 90,
  six_months: 180,
};
